'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  Calendar,
  Check,
  Copy,
  ExternalLink,
  Gem,
  GraduationCap,
  Grid3X3,
  Landmark,
  LayoutGrid,
  LogOut,
  User2,
  X,
  Zap,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, truncateAddress } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import { PLINTH_PRESETS } from '@/features/lobby/launch/data/presets';
import type { PlinthMaterialId } from '@/features/lobby/theming/registry';
import { formatCompactUsd } from '@/features/lobby/data/mock-prices';
import { XIcon } from '@/features/lobby/ui/BrandIcons';
import {
  setPlayerPlinth,
  usePlayerPlinthMaterial,
} from '@/features/lobby/board-canvas/player-plinth-preference';
import type { HudSnapshot } from '@/lib/profile/build-hud-snapshot';
import { ProfileActivityFeed } from '@/components/profile/profile-activity-feed';
import { AvatarPlinthPreview } from '@/components/profile/avatar-plinth-preview';
import { ProfileLeaderboardSnippet } from '@/components/profile/profile-leaderboard-snippet';
import { queryKeys } from '@/lib/query-keys';
import { useWallet } from '@/hooks/use-wallet';
import { useLobbyHeaderStats } from '@/hooks/use-lobby-header-stats';
import { RewardsSheet } from '@/features/lobby/nav/RewardsSheet';

export interface ProfileHudProps {
  variant: 'page' | 'embedded';
  address: string;
  displayName: string;
  avatarUrl: string | null;
  level: number;
  isOwnProfile: boolean;
  onClose?: () => void;
  copied: boolean;
  onCopyAddress: () => void;
  userProfile: {
    twitter?: { username: string } | null;
  } | null;
  linkTwitter: () => void;
  snapshot: HudSnapshot;
  tilesTab: React.ReactNode;
  gridsTab: React.ReactNode;
  defaultTab?: 'activity' | 'boards' | 'tiles';
  /** Own profile: opens avatar update flow */
  onEditAvatar?: () => void;
  /** Portfolio / seats fetch still in flight — snapshot + tiles can skeleton independently of boards/activity. */
  portfolioLoading?: boolean;
}

export function ProfileHud({
  variant,
  address,
  displayName,
  avatarUrl,
  level,
  isOwnProfile,
  onClose,
  copied,
  onCopyAddress,
  userProfile,
  linkTwitter,
  snapshot,
  tilesTab,
  gridsTab,
  defaultTab = 'activity',
  onEditAvatar,
  portfolioLoading = false,
}: ProfileHudProps) {
  const localPlinth = usePlayerPlinthMaterial();

  const { data: plinthJson } = useQuery({
    queryKey: queryKeys.userPlinth(address.toLowerCase()),
    queryFn: async () => {
      const r = await fetch(`/api/users/${encodeURIComponent(address.toLowerCase())}/plinth`);
      if (!r.ok) return { plinthId: null as string | null };
      const j = await r.json();
      return j.data as { plinthId: string | null };
    },
    enabled: !!address,
    staleTime: 60_000,
    retry: 1,
  });

  const serverPlinthId = plinthJson?.plinthId ?? null;
  const displayPlinthId = (
    isOwnProfile ? serverPlinthId ?? localPlinth : serverPlinthId ?? 'stone'
  ) as PlinthMaterialId;

  const activePlinth =
    PLINTH_PRESETS.find((p) => p.id === displayPlinthId) ?? PLINTH_PRESETS[0];

  useEffect(() => {
    if (isOwnProfile && serverPlinthId) {
      setPlayerPlinth(serverPlinthId as PlinthMaterialId);
    }
  }, [isOwnProfile, serverPlinthId]);

  // `tab` is hoisted to a controlled value so the mobile "Tiles"
  // action button (in the wallet-actions row below) can deep-link
  // straight into the Tiles tab without making the user hunt for the
  // small tab strip above the activity feed. Defaults to whatever the
  // caller asked for (`activity`) but stays controlled the whole
  // session so the tab strip's clicks don't bypass our state.
  const [tab, setTab] = useState<NonNullable<ProfileHudProps['defaultTab']>>(defaultTab);

  // Wallet actions are only relevant when this is the viewer's own
  // profile. We pull `logout` straight from `useWallet` (the same
  // hook the desktop wallet dropdown uses) so the disconnect path is
  // the canonical Privy logout — no divergent flow on mobile. The
  // claim badge / count comes from `useLobbyHeaderStats` so it
  // matches the bottom-nav profile gem and the desktop rewards pill.
  const { logout: walletLogout } = useWallet();
  const { hasAnyClaimable, claimableUsd } = useLobbyHeaderStats();
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const router = useRouter();
  const showWalletActions = isOwnProfile;

  return (
    <div
      className={cn(
        variant === 'embedded' && 'w-full',
        variant === 'page' && 'w-full max-w-[960px] mx-auto',
      )}
    >
      {variant === 'embedded' && (
        <header className="flex items-center justify-between gap-3 border-b border-amber-500/20 px-1 pb-3 mb-0">
          <div className="text-amber-100 flex items-center gap-2 text-sm font-semibold">
            <User2 className="h-4 w-4" />
            Profile
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-[4px] border border-amber-500/25 p-1.5 text-zinc-400 hover:text-amber-100 hover:border-amber-400/50 transition-colors"
              aria-label="Close profile"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </header>
      )}

      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-[360px_1fr] gap-0 min-h-0',
          variant === 'embedded' && 'md:h-[min(640px,78vh)]',
          variant === 'page' && 'md:min-h-[560px]',
        )}
      >
        {/* `[&>*]:shrink-0` keeps every section in the aside (header,
            avatar/plinth hero, Socials, leaderboard) at its design
            height. Without it the default `flex-shrink: 1` was
            collapsing the 240px AvatarPlinthPreview down to a thin
            strip on desktop whenever the column had to fit the
            leaderboard snippet too — the user reported the avatar /
            plinth / level "lost its vertical height". With shrink
            disabled, the aside's `overflow-y-auto` engages instead
            and stacks scroll naturally. */}
        <aside className="border-b md:border-b-0 md:border-r border-amber-500/15 flex flex-col overflow-y-auto [&>*]:shrink-0">
          <div className="px-4 py-3 border-b border-amber-500/10">
            <div className="flex items-center gap-2">
              <AvatarWithFlair
                avatarUrl={avatarUrl}
                address={address}
                level={level}
                size="xs"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-zinc-100 truncate leading-tight">
                  {displayName.startsWith('@') ? displayName : `@${displayName}`}
                </div>
                <button
                  type="button"
                  onClick={onCopyAddress}
                  className="group mt-0.5 flex items-center gap-1 text-[10px] font-mono tracking-[0.1em] text-zinc-400 hover:text-amber-100 transition-colors"
                  title={copied ? 'Copied!' : 'Copy address'}
                >
                  <span className="tabular-nums">{truncateAddress(address)}</span>
                  {copied ? (
                    <Check className="h-3 w-3 text-emerald-300" />
                  ) : (
                    <Copy className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <AvatarPlinthPreview
            plinth={activePlinth}
            avatarUrl={avatarUrl}
            address={address}
            level={level}
            editable={isOwnProfile && !!onEditAvatar}
            onEditClick={onEditAvatar}
          />

          <div className="px-4 py-3 border-t border-amber-500/10">
            <SectionLabel>Socials</SectionLabel>
            {userProfile?.twitter ? (
              <a
                href={`https://x.com/${userProfile.twitter.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-2 rounded-[4px] border text-[11px] font-medium transition-colors',
                  'bg-black/40 border-white/10 text-zinc-200 hover:border-sky-400/40',
                )}
              >
                <span
                  className="w-5 h-5 grid place-items-center rounded-[3px] bg-white/10 text-white"
                  aria-hidden
                >
                  <XIcon size={10} />
                </span>
                <span className="flex-1 text-left">
                  <span className="font-mono">@{userProfile.twitter.username}</span>
                  <span className="ml-1.5 text-[9px] font-mono tracking-[0.18em] uppercase text-emerald-300">
                    linked
                  </span>
                </span>
                <ExternalLink className="h-3 w-3 opacity-70" />
              </a>
            ) : isOwnProfile ? (
              <button
                type="button"
                onClick={() => linkTwitter()}
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-2 rounded-[4px] border text-[11px] font-medium transition-colors',
                  'bg-gradient-to-b from-sky-500/10 to-sky-500/[0.02] border-sky-400/30 text-sky-100 hover:border-sky-300/60 hover:from-sky-500/20',
                )}
                title="Connect your X account"
              >
                <span
                  className="w-5 h-5 grid place-items-center rounded-[3px] bg-sky-400/20 text-sky-100"
                  aria-hidden
                >
                  <XIcon size={10} />
                </span>
                <span className="flex-1 text-left">Connect X</span>
                <ExternalLink className="h-3 w-3 opacity-70" />
              </button>
            ) : (
              <p className="text-[11px] text-zinc-500 font-mono">No X linked</p>
            )}
          </div>

          {/* Mobile-only wallet actions row.
              The user explicitly asked for tiles / claims / disconnect
              to be reachable from the connected-wallet profile modal
              on mobile — desktop already has those affordances on the
              top nav (rewards pill + wallet dropdown), but the mobile
              shell strips that chrome to a 3-tab bottom nav so the
              entry points have to live inside the modal itself.

              `md:hidden` because desktop keeps the LobbyNav rewards
              pill and WalletButton dropdown above the modal — adding
              these here would duplicate them. The row sits between
              Socials and the leaderboard so it's the first thing the
              user sees after the avatar/socials when the modal opens
              full-screen. */}
          {showWalletActions && (
            <div className="md:hidden px-4 py-3 border-t border-amber-500/10">
              <SectionLabel>Wallet</SectionLabel>
              {/* 2x2 grid since we now also surface the Tutorial entry
                  here — the bottom nav lost the dedicated Tutorial tab
                  to make room for Chat + a notifications bell on the
                  Profile avatar, so the offchain walkthrough now hangs
                  off the connected-wallet profile modal instead. */}
              <div className="grid grid-cols-2 gap-2">
                <WalletActionButton
                  label="Tiles"
                  sublabel={`${snapshot.totalSeats}`}
                  icon={<LayoutGrid className="h-3 w-3" />}
                  tone="default"
                  onClick={() => setTab('tiles')}
                />
                <WalletActionButton
                  label="Claims"
                  sublabel={
                    hasAnyClaimable ? formatCompactUsd(claimableUsd) : 'None'
                  }
                  icon={<Gem className="h-3 w-3" />}
                  tone={hasAnyClaimable ? 'positive' : 'muted'}
                  onClick={() => setRewardsOpen(true)}
                />
                <WalletActionButton
                  label="Tutorial"
                  sublabel="Walkthrough"
                  icon={<GraduationCap className="h-3 w-3" />}
                  tone="default"
                  onClick={() => {
                    onClose?.();
                    router.push('/tutorial');
                  }}
                />
                <WalletActionButton
                  label="Disconnect"
                  sublabel="Sign out"
                  icon={<LogOut className="h-3 w-3" />}
                  tone="danger"
                  onClick={() => {
                    void walletLogout();
                    onClose?.();
                  }}
                />
              </div>
            </div>
          )}

          <ProfileLeaderboardSnippet address={address} />
        </aside>

        <main className="overflow-y-auto min-h-0 flex flex-col">
          <section className="px-5 pt-4 pb-3 border-b border-amber-500/10 shrink-0">
            <SectionLabel>Snapshot</SectionLabel>
            {portfolioLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-[72px] rounded-[4px] border border-amber-500/10 bg-amber-950/30"
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <MetricCard
                  icon={<LayoutGrid className="h-3 w-3" />}
                  label="Tile value"
                  value={formatCompactUsd(snapshot.tileValueUsd)}
                  sublabel={`${snapshot.totalSeats} tiles · ${snapshot.boardCount} boards`}
                />
                <MetricCard
                  icon={<Calendar className="h-3 w-3" />}
                  label="Player since"
                  value={snapshot.sinceLabel}
                  sublabel={`${snapshot.daysPlayed}d · Lvl ${level}`}
                />
                <MetricCard
                  icon={<Gem className="h-3 w-3" />}
                  label="Rewards claimed"
                  value={formatCompactUsd(snapshot.lifetimeRewardsUsd)}
                  sublabel="Lifetime"
                  tone="positive"
                />
                <MetricCard
                  icon={<Landmark className="h-3 w-3" />}
                  label="Taxes claimed"
                  value={formatCompactUsd(snapshot.lifetimeTaxesUsd)}
                  sublabel="Lifetime"
                  tone="positive"
                />
              </div>
            )}
          </section>

          <section className="flex-1 min-h-0 flex flex-col px-5 py-3">
            {/* Controlled `tab` state — parent owns the active tab so
                the mobile wallet-actions "Tiles" button (above) can
                deep-link straight to the Tiles list without making
                the user reach for the small tab strip themselves. */}
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as NonNullable<ProfileHudProps['defaultTab']>)}
              className="flex flex-col flex-1 min-h-0 gap-2"
            >
              <div className="flex items-center justify-between gap-2 shrink-0">
                <TabsList className="w-full max-w-md bg-black/40 border border-white/[0.06] p-0.5">
                  <TabsTrigger
                    value="activity"
                    className="flex-1 gap-1 text-[10px] font-mono uppercase tracking-[0.15em] data-[state=active]:bg-amber-500/15"
                  >
                    <Zap className="h-3 w-3" />
                    Activity
                  </TabsTrigger>
                  <TabsTrigger
                    value="boards"
                    className="flex-1 gap-1 text-[10px] font-mono uppercase tracking-[0.15em] data-[state=active]:bg-amber-500/15"
                  >
                    <Grid3X3 className="h-3 w-3" />
                    Boards
                  </TabsTrigger>
                  <TabsTrigger
                    value="tiles"
                    className="flex-1 gap-1 text-[10px] font-mono uppercase tracking-[0.15em] data-[state=active]:bg-amber-500/15"
                  >
                    <LayoutGrid className="h-3 w-3" />
                    Tiles
                  </TabsTrigger>
                </TabsList>
              </div>

              {/*
                Tab bodies participate in the outer `main`'s flex/scroll
                instead of each carrying their own `max-h-[min(52vh,420px)]`.
                The previous dual-scroll setup capped the tab content at
                ~420px, so on mobile (and small modals) the bottom of the
                boards / tiles / activity lists was unreachable — the
                outer modal wasn't scrolled past the tab bar because its
                own overflow was already "handled" by the inner box.
                With `flex-1 min-h-0`, the tabs stretch to fill whatever
                height the modal has left and scroll as a single surface.
              */}
              <TabsContent
                forceMount
                value="activity"
                className="mt-2 flex-1 min-h-0 overflow-hidden flex flex-col data-[state=inactive]:hidden"
              >
                <ProfileActivityFeed
                  address={address}
                  viewerIsSubject={isOwnProfile}
                />
              </TabsContent>

              <TabsContent
                forceMount
                value="boards"
                className="mt-2 flex-1 min-h-0 overflow-y-auto space-y-3 data-[state=inactive]:hidden"
              >
                {gridsTab}
              </TabsContent>

              <TabsContent
                forceMount
                value="tiles"
                className="mt-2 flex-1 min-h-0 overflow-y-auto space-y-3 data-[state=inactive]:hidden"
              >
                {tilesTab}
              </TabsContent>
            </Tabs>
          </section>
        </main>
      </div>
      {/* RewardsSheet uses Radix `Sheet` which portals to <body>, so it
          floats above the profile modal correctly even when the modal
          itself is `position: fixed`. Mount it once here so the mobile
          wallet-actions Claim button has somewhere to open into without
          having to plumb a global rewards-open context through the
          modal-host portal chain. */}
      {showWalletActions && (
        <RewardsSheet open={rewardsOpen} onOpenChange={setRewardsOpen} />
      )}
    </div>
  );
}

/** Compact tile button used in the mobile wallet-actions row. Three of
 *  these sit in a 3-col grid (Tiles / Claims / Disconnect) so they all
 *  read at the same weight regardless of which tone is active.
 *  `tone='positive'` paints amber when there's an actionable claim,
 *  `'danger'` paints rose for the disconnect path, and `'muted'` is
 *  the resting style when claims are zero. */
function WalletActionButton({
  label,
  sublabel,
  icon,
  tone = 'default',
  onClick,
}: {
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  tone?: 'default' | 'positive' | 'muted' | 'danger';
  onClick: () => void;
}) {
  const toneClass =
    tone === 'positive'
      ? 'border-amber-400/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20'
      : tone === 'danger'
        ? 'border-rose-400/30 bg-rose-500/5 text-rose-100 hover:bg-rose-500/15'
        : tone === 'muted'
          ? 'border-white/10 bg-black/40 text-zinc-500 hover:text-zinc-200 hover:border-white/20'
          : 'border-amber-500/20 bg-black/40 text-zinc-200 hover:border-amber-400/40 hover:text-amber-100';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 rounded-[4px] border px-2 py-2 transition-colors',
        toneClass,
      )}
    >
      <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.18em]">
        {icon}
        {label}
      </span>
      {sublabel && (
        <span className="mt-0.5 text-[11px] font-mono tabular-nums leading-none">
          {sublabel}
        </span>
      )}
    </button>
  );
}

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mb-2 text-[9px] tracking-[0.28em] font-mono uppercase text-zinc-500',
        className,
      )}
    >
      {children}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sublabel,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  tone?: 'default' | 'positive' | 'negative';
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-emerald-200'
      : tone === 'negative'
        ? 'text-orange-200'
        : 'text-amber-100';
  return (
    <div className="rounded-[4px] border border-white/[0.06] bg-black/40 px-2.5 py-2">
      <div className="flex items-center gap-1 text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500">
        <span className="text-amber-200/80">{icon}</span>
        {label}
      </div>
      <div className={cn('mt-0.5 text-[16px] font-mono tabular-nums font-semibold', toneClass)}>
        {value}
      </div>
      {sublabel && (
        <div className="text-[9px] font-mono tracking-[0.15em] uppercase text-zinc-500">{sublabel}</div>
      )}
    </div>
  );
}

